import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Eye, SkipForward } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { EmptyReminders, ErrorState } from '../components/ui/EmptyState';
import AddReminderModal from '../components/reminders/AddReminderModal';
import EditReminderModal from '../components/reminders/EditReminderModal';
import DeleteReminderModal from '../components/reminders/DeleteReminderModal';
import ViewReminderModal from '../components/reminders/ViewReminderModal';
import { formatDate } from '../utils/dateUtils';
import { useReminders, useAdvanceReminder } from '../hooks/useReminders';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const statusStyles = {
    overdue: 'bg-status-overdue-bg text-status-overdue',
    active: 'bg-status-paid-bg text-status-paid',
    inactive: 'bg-surface-overlay text-text-muted',
};

const getReminderStatus = (reminder) => {
    if (!reminder.is_active) return 'inactive';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(reminder.due_date);
    dueDate.setHours(0, 0, 0, 0);
    if (dueDate < today) return 'overdue';
    return 'active';
};

const Reminders = () => {
    const { data: reminders = [], isLoading: loading, error, refetch } = useReminders();
    const advanceReminder = useAdvanceReminder();
    const { t } = useLanguage();

    const statusLabels = {
        overdue: t(KEYS.REMINDERS_STATUS_OVERDUE),
        active: t(KEYS.REMINDERS_STATUS_ACTIVE),
        inactive: t(KEYS.REMINDERS_STATUS_INACTIVE),
    };
    const frequencyLabelMap = {
        once: t(KEYS.REMINDER_FREQ_ONCE),
        daily: t(KEYS.REMINDER_FREQ_DAILY),
        weekly: t(KEYS.REMINDER_FREQ_WEEKLY),
        monthly: t(KEYS.REMINDER_FREQ_MONTHLY),
        quarterly: t(KEYS.REMINDER_FREQ_QUARTERLY),
        yearly: t(KEYS.REMINDER_FREQ_YEARLY),
    };
    const frequencyLabel = (freq) => freq ? (frequencyLabelMap[freq] || freq) : '—';

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState(null);
    const [advancingUid, setAdvancingUid] = useState(null);
    const [advanceError, setAdvanceError] = useState('');

    const handleView = (reminder) => {
        setSelectedReminder(reminder);
        setIsViewModalOpen(true);
    };

    const handleEdit = (reminder) => {
        setSelectedReminder(reminder);
        setIsEditModalOpen(true);
    };

    const handleDelete = (reminder) => {
        setSelectedReminder(reminder);
        setIsDeleteModalOpen(true);
    };

    const handleAdvance = async (reminder) => {
        setAdvancingUid(reminder.uid);
        setAdvanceError('');
        try {
            await advanceReminder.mutateAsync({ reminderUid: reminder.uid });
        } catch (err) {
            const msg = err.errors?.reminder ?? err.message ?? 'Failed to advance reminder.';
            setAdvanceError(msg);
        } finally {
            setAdvancingUid(null);
        }
    };

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary">
                        {t(KEYS.REMINDERS_TITLE)}
                    </h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.REMINDERS_SUBTITLE)}
                    </p>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.REMINDERS_ADD)}
                </Button>
            </div>

            {/* Content */}
            {advanceError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {advanceError}
                </div>
            )}
            {loading ? (
                <SkeletonTable rows={5} columns={5} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.REMINDERS_ERROR_LOAD)} onRetry={refetch} />
            ) : reminders && reminders.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.REMINDERS_COL_TITLE)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.REMINDERS_COL_DUE_DATE)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">
                                        {t(KEYS.REMINDERS_COL_FREQUENCY)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.REMINDERS_COL_STATUS)}
                                    </th>
                                    <th className="px-4 py-2.5 w-28"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {reminders.map((reminder, index) => {
                                    const status = getReminderStatus(reminder);
                                    const isAdvancing = advancingUid === reminder.uid;
                                    return (
                                        <tr
                                            key={reminder.uid}
                                            className="hover:bg-surface-overlay transition-colors group animate-appear"
                                            style={{ animationDelay: `${index * 30}ms` }}
                                        >
                                            <td className="px-4 py-2.5 text-sm text-text-primary font-medium">
                                                {reminder.title}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-text-muted">
                                                {formatDate(reminder.due_date)}
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-text-secondary hidden md:table-cell">
                                                {frequencyLabel(reminder.frequency)}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${statusStyles[status]}`}>
                                                    {statusLabels[status] || status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => handleView(reminder)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                        title="View"
                                                    >
                                                        <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(reminder)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                    {reminder.is_active && (
                                                        <button
                                                            onClick={() => handleAdvance(reminder)}
                                                            className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-paid rounded transition-colors"
                                                            title="Advance"
                                                            disabled={isAdvancing}
                                                        >
                                                            <SkipForward className={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${isAdvancing ? 'animate-spin' : ''}`} />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(reminder)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <EmptyReminders onAdd={() => setIsAddModalOpen(true)} />
            )}

            {/* Modals */}
            <AddReminderModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
            />
            <EditReminderModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                reminder={selectedReminder}
            />
            <DeleteReminderModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                reminder={selectedReminder}
            />
            <ViewReminderModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                reminder={selectedReminder}
                status={selectedReminder ? getReminderStatus(selectedReminder) : null}
            />
        </div>
    );
};

export default Reminders;
